import type { CourseLesson, CourseModule } from './api';

/** Where a prev/next step lands — enough to build a lesson URL. */
export interface LessonPointer {
  moduleId: string;
  lessonId: string;
}

export interface LessonView {
  /** The module named in the URL, or null if this course has no such module. */
  module: CourseModule | null;
  /** The lesson, or null if it isn't in that module. */
  lesson: CourseLesson | null;
  /** 1-based position within its own module; 0 when the lesson isn't found. */
  lessonNumber: number;
  prev: LessonPointer | null;
  next: LessonPointer | null;
}

/**
 * Everything the lesson page reads off a `/api/courses/:slug` payload for one
 * lesson: which module it sits in, where it sits inside that module, and the
 * lessons either side of it.
 *
 * **Prev/next cross module boundaries.** The module is a grouping for browsing,
 * not a wall: a learner watching straight through the course steps from the last
 * lesson of one module into the first of the next, and back again. Empty modules
 * are stepped over — they hold nothing to play. Only the true ends of the course
 * yield null, which is what lets the last lesson hand its "next" slot to the
 * bonus CTA.
 *
 * `module` and `lesson` resolve independently: a stale link can name a real
 * module and a lesson that no longer lives in it, and the module is still a real
 * surface to send that learner to.
 */
export function lessonView(
  modules: CourseModule[],
  moduleId: string,
  lessonId: string,
): LessonView {
  const moduleIndex = modules.findIndex((m) => m.id === moduleId);
  const module = moduleIndex >= 0 ? modules[moduleIndex]! : null;
  if (!module) {
    return { module: null, lesson: null, lessonNumber: 0, prev: null, next: null };
  }

  const idx = module.lessons.findIndex((l) => l.id === lessonId);
  const lesson = idx >= 0 ? module.lessons[idx]! : null;
  if (!lesson) {
    return { module, lesson: null, lessonNumber: 0, prev: null, next: null };
  }

  const prevInModule = idx > 0 ? module.lessons[idx - 1]! : null;
  const nextInModule = idx < module.lessons.length - 1 ? module.lessons[idx + 1]! : null;

  return {
    module,
    lesson,
    lessonNumber: idx + 1,
    prev: prevInModule
      ? { moduleId: module.id, lessonId: prevInModule.id }
      : lastLessonBefore(modules, moduleIndex),
    next: nextInModule
      ? { moduleId: module.id, lessonId: nextInModule.id }
      : firstLessonAfter(modules, moduleIndex),
  };
}

function lastLessonBefore(modules: CourseModule[], moduleIndex: number): LessonPointer | null {
  for (let i = moduleIndex - 1; i >= 0; i--) {
    const m = modules[i]!;
    const last = m.lessons[m.lessons.length - 1];
    if (last) return { moduleId: m.id, lessonId: last.id };
  }
  return null;
}

function firstLessonAfter(modules: CourseModule[], moduleIndex: number): LessonPointer | null {
  for (let i = moduleIndex + 1; i < modules.length; i++) {
    const m = modules[i]!;
    const first = m.lessons[0];
    if (first) return { moduleId: m.id, lessonId: first.id };
  }
  return null;
}
